import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { extractErrorMessage } from '../../utils/errorUtils';
import ConfirmDialog from '../common/ConfirmDialog';

interface FeatureFlagGrant {
  user_uuid: string;
  feature_flag_id: number;
  granted_by: string | null;
  granted_at: string;
  flag_name: string | null;
  flag_description: string | null;
}

interface FeatureFlagOption {
  id: number;
  name: string;
  description: string | null;
  enabled: boolean;
  organization_count: number;
  user_count: number;
}

interface UserFeatureFlagsTabProps {
  userUuid: string;
}

export default function UserFeatureFlagsTab({ userUuid }: UserFeatureFlagsTabProps) {
  const [grants, setGrants] = useState<FeatureFlagGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState<FeatureFlagGrant | null>(null);

  // Add dialog state
  const [flags, setFlags] = useState<FeatureFlagOption[]>([]);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlagOption | null>(null);
  const [loadingFlags, setLoadingFlags] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    loadGrants();
  }, [userUuid]);

  const loadGrants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<FeatureFlagGrant[]>(
        `/admin/users/${userUuid}/feature-flags`
      );
      setGrants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feature flag grants');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = async () => {
    setAddDialogOpen(true);
    setSelectedFlag(null);
    setAddError(null);

    try {
      setLoadingFlags(true);
      const allFlags = await apiClient.get<FeatureFlagOption[]>('/admin/feature-flags');
      const grantedIds = new Set(grants.map((g) => g.feature_flag_id));
      const available = allFlags.filter((f: FeatureFlagOption) => !grantedIds.has(f.id));
      setFlags(available);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to load feature flags');
    } finally {
      setLoadingFlags(false);
    }
  };

  const handleAddGrant = async () => {
    if (!selectedFlag) return;

    try {
      setAddLoading(true);
      setAddError(null);
      await apiClient.post(`/admin/users/${userUuid}/feature-flags`, {
        feature_flag_id: selectedFlag.id,
      });
      setAddDialogOpen(false);
      await loadGrants();
    } catch (err: unknown) {
      setAddError(extractErrorMessage(err, 'Failed to grant feature flag'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveGrant = (grant: FeatureFlagGrant) => {
    setSelectedGrant(grant);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedGrant) return;

    try {
      await apiClient.delete(
        `/admin/users/${userUuid}/feature-flags/${selectedGrant.feature_flag_id}`
      );
      setRemoveDialogOpen(false);
      setSelectedGrant(null);
      await loadGrants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke feature flag');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Feature Flag Grants</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} size="small">
          Grant Flag
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Flag Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Date Granted</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grants.map((grant) => (
              <TableRow key={grant.feature_flag_id}>
                <TableCell>{grant.flag_name || '-'}</TableCell>
                <TableCell>{grant.flag_description || '-'}</TableCell>
                <TableCell>{formatDate(grant.granted_at)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveGrant(grant)}
                    title="Revoke flag"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {grants.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No feature flag grants yet
          </Typography>
        </Box>
      )}

      {/* Grant Feature Flag Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Feature Flag</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {addError && (
              <Alert severity="error" onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}
            {loadingFlags ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Autocomplete
                options={flags}
                getOptionLabel={(option) => option.name}
                value={selectedFlag}
                onChange={(_, newValue) => setSelectedFlag(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Feature Flag" placeholder="Search flags..." />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGrant}
            variant="contained"
            disabled={addLoading || !selectedFlag}
          >
            {addLoading ? 'Granting...' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <ConfirmDialog
        open={removeDialogOpen}
        title="Revoke Feature Flag"
        message={`Are you sure you want to revoke the "${selectedGrant?.flag_name}" flag from this user?`}
        confirmText="Revoke"
        confirmColor="error"
        onConfirm={handleConfirmRemove}
        onCancel={() => {
          setRemoveDialogOpen(false);
          setSelectedGrant(null);
        }}
      />
    </Box>
  );
}
